import {
  FileAudio,
  FileText,
  FileVolume,
  Home,
  Settings,
  Users,
  type LucideIcon,
} from "lucide-react";

export type NavigationRole = "admin" | "user";
export type NavigationGroup = "primary" | "secondary";
export type NavigationMatchMode = "exact" | "prefix";

export interface NavigationItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  roles: NavigationRole[];
  group: NavigationGroup;
  match: NavigationMatchMode;
  includeInUserMenu?: boolean;
}

const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: "home",
    label: "Home",
    to: "/home",
    icon: Home,
    roles: ["admin", "user"],
    group: "primary",
    match: "exact",
    includeInUserMenu: true,
  },
  {
    id: "audio-files",
    label: "Audio Files",
    to: "/audio-files",
    icon: FileAudio,
    roles: ["admin", "user"],
    group: "primary",
    match: "prefix",
  },
  {
    id: "audio-file-sections",
    label: "Audio File Sections",
    to: "/audio-file-sections",
    icon: FileVolume,
    roles: ["admin", "user"],
    group: "primary",
    match: "prefix",
  },
  {
    id: "reports",
    label: "Reports",
    to: "/admin/reports",
    icon: FileText,
    roles: ["admin"],
    group: "primary",
    match: "exact",
    includeInUserMenu: true,
  },
  {
    id: "users",
    label: "User Management",
    to: "/admin/users",
    icon: Users,
    roles: ["admin"],
    group: "secondary",
    match: "exact",
    includeInUserMenu: true,
  },
  {
    id: "settings",
    label: "Settings",
    to: "/settings",
    icon: Settings,
    roles: ["admin", "user"],
    group: "secondary",
    match: "exact",
    includeInUserMenu: true,
  },
];

export function getNavigationItemsForRole(role?: string | null): NavigationItem[] {
  if (!role) {
    return [];
  }

  return NAVIGATION_ITEMS.filter((item) => item.roles.includes(role as NavigationRole));
}

export function getNavigationGroups(role?: string | null): Record<NavigationGroup, NavigationItem[]> {
  const items = getNavigationItemsForRole(role);

  return {
    primary: items.filter((item) => item.group === "primary"),
    secondary: items.filter((item) => item.group === "secondary"),
  };
}

export function getUserMenuItems(role?: string | null): NavigationItem[] {
  return getNavigationItemsForRole(role).filter((item) => item.includeInUserMenu);
}

export function isNavigationItemActive(item: NavigationItem, pathname: string): boolean {
  if (item.match === "prefix") {
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  }

  return pathname === item.to;
}
